<template>
  <v-dialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    max-width="700"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
    @click:outside="!showSummary && closeDialog()"
    @keydown.esc="showSummary ? goBack() : closeDialog()"
    @shortkey.native="showSummary ? goBack() : closeDialog()"
  >
    <v-form novalidate @submit.prevent>
      <v-card>
        <v-card-title>
          <span class="headline"> New <format-side :side="side" /> Sponsored Position </span>
        </v-card-title>

        <v-card-text>
          <v-container v-if="!showSummary">
            <v-row class="mt-n4" dense>
              <v-col cols="6">
                <counterparty-search
                  v-model="counterparty"
                  :autofocus="!counterparty"
                  clearable
                  :error-messages="errorMsgs['counterparty']"
                  label="Counterparty"
                  sponsored
                />
              </v-col>
              <v-col cols="6">
                <simple-security-search
                  v-model="security"
                  :error-messages="errorMsgs['security']"
                  label="Security"
                  @change="
                    $v.$reset();
                    $v.security.$touch();
                  "
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="6">
                <numeric-input
                  v-model="quantity"
                  :error-messages="errorMsgs['quantity']"
                  label="Quantity"
                  :min="0"
                  :step="100"
                  type="integer"
                  @change="
                    $v.$reset();
                    $v.quantity.$touch();
                  "
                />
              </v-col>
              <v-col cols="6">
                <numeric-input
                  v-model="rate"
                  :autofocus="!!security"
                  :error-messages="errorMsgs['rate']"
                  label="Rate"
                  :precision="ratePrecision"
                  :step="0.25"
                  suffix="%"
                  type="decimal"
                  @blur="$v.rate.$touch()"
                  @change="
                    $v.$reset();
                    $v.rate.$touch();
                  "
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="6">
                <v-select
                  v-model="rateType"
                  class="mt-n2"
                  :items="['Fixed', 'Floating']"
                  @change="onChangeRateType"
                />
              </v-col>
              <v-col v-if="rateType === 'Floating'" cols="6">
                <benchmark-select
                  v-model="benchmark"
                  class="mt-n2"
                  :error-messages="errorMsgs.benchmark"
                  @blur="$v.benchmark.$touch()"
                  @input="$v.benchmark.$touch()"
                />
              </v-col>
            </v-row>
          </v-container>

          <v-container v-if="showSummary" v-shortkey="['enter']" @shortkey="submitForm()">
            <v-row class="text--primary">
              <v-col class="py-0">
                <h2 class="text-h6 text--secondary">Continue?</h2>
                <v-row class="d-flex align-start">
                  <v-col cols="6 border-right-divider">
                    <table class="summary">
                      <tbody>
                        <tr>
                          <td>Counterparty</td>
                          <td>
                            {{ counterparty ? formatCompanyName(counterparty) : 'Any' }}
                          </td>
                        </tr>
                        <tr>
                          <td>Security</td>
                          <td>{{ security ? `${security.ticker} [${security.cusip}]` : '' }}</td>
                        </tr>
                        <tr>
                          <td>Side</td>
                          <td>
                            <format-side :side="side" />
                          </td>
                        </tr>
                        <tr>
                          <td>Quantity</td>
                          <td>
                            <pretty-number :value="quantity" />
                          </td>
                        </tr>
                        <tr>
                          <td>Rate</td>
                          <td>
                            <span v-if="rate === null">–</span>
                            <rate-output v-if="benchmark" :rate="rate" :rate-modifier="benchmark" />
                            <rate-output v-else :rate="rate" />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </v-col>
                  <v-col cols="6 pl-6">
                    <table class="summary">
                      <tbody>
                        <tr>
                          <td>IA Limit</td>
                          <td>
                            <span v-if="independentAmountRate.toNumber() === 0">Any</span>
                            <rate-output v-else :precision="2" :rate="independentAmountRate" />
                          </td>
                        </tr>
                        <tr>
                          <td>Rounding Rule</td>
                          <td>
                            {{ roundingRuleToShortString(roundingRule) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-divider />
              </v-col>
            </v-row>

            <v-row v-if="showError">
              <v-col class="pa-0 px-1 col-6 offset-3">
                <div
                  class="v-alert v-alert--dense text--primary text-body-2 text-center"
                  :class="{ error: showError }"
                >
                  <div v-if="showError">
                    {{ errorMsgs.apiErrors.join('\n') }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions class="d-flex">
          <div class="d-flex flex-grow-1 justify-space-between align-end">
            <template v-if="!showSummary">
              <v-btn color="secondary" @click="closeDialog">Cancel</v-btn>
              <v-btn
                color="primary"
                data-test="confirm-button"
                type="submit"
                @click="goToSummary()"
              >
                Create
              </v-btn>
            </template>

            <template v-if="showSummary">
              <v-btn color="secondary" :disabled="formStatus !== 'idle'" @click="goBack()">
                Back
              </v-btn>
              <aurora-btn
                color="primary"
                data-test="create-button"
                :disabled="formStatus !== 'idle'"
                :loading="formStatus === 'submitting'"
                timeframe="createLoans"
                @click="submitForm"
              >
                Create
              </aurora-btn>
            </template>
          </div>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script lang="ts">
import Component, { mixins } from 'vue-class-component';
import { PropType } from 'vue';
import { mapState } from 'vuex';
import { validationMixin } from 'vuelidate';
import {
  decimal,
  integer,
  maxValue,
  minValue,
  required,
  requiredIf,
} from 'vuelidate/lib/validators';
import { notGreaterThanPrecision } from '@/utils/helpers/custom-validators';
import { MAX_RATE, RATE_PRECISION } from '@/modules/common/constants/precision';
import Decimal from 'decimal.js';
import { DialogFormStatus } from '@/modules/common/types/dialog';
import { CompanyInfo, Security } from '@/modules/common/models';
import SimpleSecuritySearch from '@/modules/manual-loan/components/SimpleSecuritySearch.vue';
import CounterpartySearch from '@/modules/user-accounts/components/CounterpartySearch.vue';
import BtnDropdown from '@/modules/common/components/BtnDropdown.vue';
import {
  RoundingRule,
  roundingRuleToShortString,
} from '@/modules/sec-lending/helpers/contract-details';
import { calculateOrderNotional, formatDecimalAsString } from '@/utils/helpers/auction-numbers';
import { RiskLimitValidator } from '@/utils/helpers/risk-limit-validator';
import { LoginState } from '@/store/store';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { ApiError } from '@/utils/errors';
import { formatCompanyName } from '@/modules/user-accounts/helpers/user-accounts';
import { RateType } from '@/modules/marketdata/types/marketdata';
import BenchmarkSelect from '@/modules/marketdata/components/BenchmarkSelect.vue';
import { Benchmark } from '@/utils/api/loans';
import i18n from '@/localisation/i18n';
import { SponsoredLoanCreateRequest } from '@/modules/sponsorship/models';

interface FormErrors {
  apiErrors: string[];
  counterparty: string[];
  security: string[];
  quantity: string[];
  rate: string[];
  benchmark: string[];
}

@Component({
  props: {
    side: {
      type: String as PropType<'BORROW' | 'LOAN'>,
      required: true,
    },
  },
  components: {
    SimpleSecuritySearch,
    CounterpartySearch,
    BtnDropdown,
    BenchmarkSelect,
  },
  computed: {
    ...mapState(['loginState']),
  },
  mixins: [validationMixin],
  validations: function (this: SponsorshipPositionDialog) {
    return {
      counterparty: { required },
      security: { required },
      quantity: {
        integer,
        required,
        minValue: minValue(1),
      },
      rate: {
        decimal,
        required,
        notGreaterThanPrecision: notGreaterThanPrecision(RATE_PRECISION),
        minValue: minValue(-MAX_RATE),
        maxValue: maxValue(MAX_RATE),
      },
      benchmark: {
        required: requiredIf((vm: SponsorshipPositionDialog) => vm.rateType === 'Floating'),
      },
    };
  },
})
export default class SponsorshipPositionDialog extends mixins(validationMixin) {
  // props
  public readonly side!: 'BORROW' | 'LOAN';

  // store state
  public loginState!: NonNullableAll<LoginState>;

  public security: Security | null = null;
  public formStatus: DialogFormStatus = 'idle';
  public showSummary = false;
  public apiErrors: string[] = [];
  public rate: Decimal | null = null;
  public ratePrecision = RATE_PRECISION;
  public rateType: RateType = 'Fixed';
  public benchmark: Benchmark | null = null;
  public quantity: number | null = null;
  public counterparty: CompanyInfo | null = null;
  public expandedPanel: number | null = null;
  public roundingRuleToShortString = roundingRuleToShortString;

  public get errorMsgs(): FormErrors {
    const errors: FormErrors = {
      apiErrors: this.apiErrors,
      counterparty: [],
      security: [],
      quantity: [],
      rate: [],
      benchmark: [],
    };

    if (this.$v.counterparty.$dirty) {
      if (!this.$v.counterparty.required) errors.counterparty.push('please select a counterparty.');
    }

    if (this.$v.security.$dirty) {
      if (!this.$v.security.required) errors.security.push('please select a security.');
      if (this.security && this.security.cannotTradeMessage) {
        errors.security.push(i18n.t(this.security.cannotTradeMessage) as string);
      }
    }

    if (this.$v.quantity.$dirty) {
      if (!this.$v.quantity.required) errors.quantity.push('please enter a quantity.');
      if (!this.$v.quantity.integer) errors.quantity.push('please enter a valid quantity.');
      if (!this.$v.quantity.minValue) errors.quantity.push('must be greater than 0.');
    }

    if (this.$v.rate.$dirty) {
      if (!this.$v.rate.required) errors.rate.push('rate is required.');
      if (this.rate && !this.$v.rate.notGreaterThanPrecision)
        errors.rate.push(`rate must not be more than ${RATE_PRECISION} decimal places.`);
      if (!this.$v.rate.minValue) {
        errors.rate.push(`Rate cannot be lower than -${this.formatRate(MAX_RATE)}.`);
      }
      if (!this.$v.rate.maxValue) {
        errors.rate.push(`Rate cannot be more than ${this.formatRate(MAX_RATE)}.`);
      }
    }

    if (this.$v.benchmark.$dirty) {
      if (!this.$v.benchmark.required) errors.benchmark.push('please select a benchmark.');
    }

    return errors;
  }

  public get showError(): boolean {
    return !!this.errorMsgs.apiErrors.length;
  }

  public get independentAmountRate(): Decimal {
    return this.loginState.user.companyPreferredIndependentAmountRate;
  }

  public get roundingRule(): RoundingRule {
    return this.loginState.user.companyPreferredRoundingRule === null
      ? RoundingRule.NoRounding
      : this.loginState.user.companyPreferredRoundingRule;
  }

  public async submitForm(): Promise<void> {
    if (this.formStatus !== 'idle' || !this.validateForm()) {
      return;
    }
    this.formStatus = 'submitting';

    try {
      await this.$api.sponsorship.createLoans(
        SponsoredLoanCreateRequest.fromData({
          side: this.side,
          security: this.security as Security,
          counterparty: this.counterparty as CompanyInfo,
          quantity: this.quantity as number,
          rate: this.rate as Decimal,
          benchmark: this.benchmark,
        })
      );
      this.closeDialog();
      this.$snackbar.confirm('Sponsored position successfully created.');
    } catch (err) {
      const errorMessage = new ApiError(i18nServerMessage(err as Error)).message;
      this.apiErrors = [`Operation Failed: ${errorMessage}`];
      this.formStatus = 'idle';
    }
  }

  public validateForm(): boolean {
    this.$v.$reset();
    this.apiErrors = [];
    this.$v.$touch();
    return !this.$v.$anyError;
  }

  public goToSummary(): void {
    if (!this.validateForm()) {
      return;
    }
    const notional = calculateOrderNotional(
      this.quantity,
      (this.security as Security).lastClosePrice
    );
    // // will stop workflow and display soft/hard limits dialog if needed
    new RiskLimitValidator(this.$dialog, this.loginState.user).checkAndConfirmRiskLimits(
      notional,
      () => (this.showSummary = true)
    );
  }

  public goBack(): void {
    this.showSummary = false;
    this.$v.$reset();
  }

  public closeDialog(): void {
    this.$emit('close-modal');
  }

  public onChangeRateType(rateType: RateType): void {
    this.rateType = rateType;
    if (rateType === 'Fixed') {
      this.benchmark = null;
    }
  }

  public formatRate(rate: number): string {
    return formatDecimalAsString(new Decimal(rate), this.ratePrecision);
  }

  public formatCompanyName(counterparty: CompanyInfo): string {
    return formatCompanyName(counterparty);
  }
}
</script>

<style lang="scss" scoped>
.col-6 + .col-6 {
  padding-left: 12px;
}

::v-deep {
  .v-expansion-panel-content__wrap {
    padding: 0;
  }
}

.border-right-divider {
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}

.summary {
  width: 100%;
  line-height: 2;
}

.summary td:first-child {
  width: 40%;
  color: #888;
}

.summary td:last-child {
  width: 60%;
  font-weight: bold;
  color: white;
}
</style>
